// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import {
  getSupplierNumberOfDeliveries,
  getSupplierTotalWeightTons,
  getSupplierNumberOfClaims,
} from "./SupplierDataService";
import * as XLSX from 'xlsx';

export function getTableEntriesFromDeliveriesData(supplierDeliveriesData) {
  const tableEntries = {};

  tableEntries.deliveriesCount = {
    title: "deliveries",
    supplierEntries: extractNumberOfDeliveries(supplierDeliveriesData),
    formatterHeader: (value) => formatNumber(value, 1),
    formatter: (value) => formatNumber(value, 0),
  }

  tableEntries.totalWeightTons = {
    title: "total_tonnage",
    supplierEntries: extractTotalWeightTons(supplierDeliveriesData),
    formatterHeader: (value) => formatNumber(value, 1),
    formatter: (value) => formatNumber(value, 1),
    tooltip: "info_total_tonnage"
  }

  tableEntries.claimsCount = {
    title: "claims",
    supplierEntries: extractNumberOfClaims(supplierDeliveriesData),
    formatterHeader: (value) => formatNumber(value, 1),
    formatter: (value) => formatNumber(value, 0),
    styler: styleClaimsCount,
    tooltip: "info_claims"
  }

  tableEntries.claimsPerDelivery = {
    title: "claims_per_delivery",
    supplierEntries: extractClaimsPerDelivery(tableEntries.claimsCount.supplierEntries, tableEntries.deliveriesCount.supplierEntries),
    formatterHeader: (value) => formatNumber(value, 2),
    formatter: (value) => formatNumber(value, 2),
    styler: styleClaimsPer20Tons,
    tooltip: "info_claims_per_delivery"
  }

  calculateAverages(tableEntries);

  return tableEntries;
}

export function getTableEntriesFromObservationsData(supplierData) {
  const tableEntries = {};

  const flattenedDataPerLabelset = flattenSupplierDataPerLabelset(supplierData);

  tableEntries.totalBalesInProduction = {
    title: "total_bales_in_production",
    supplierEntries: extractTotalBalesInProduction(flattenedDataPerLabelset),
    formatterHeader: (value) => formatNumber(value, 0),
    formatter: (value) => formatNumber(value, 0),
    tooltip: "info_total_bales_in_production"
  }

  const brownPaperContentEntries = extractBrownPaperContent(flattenedDataPerLabelset);
  tableEntries.brownPaperContent = {
    title: "brown_paper_percentage",
    supplierEntries: brownPaperContentEntries,
    formatterHeader: (value) => formatPercentage(value, 1),
    formatter: (value) => formatPercentage(value, 1),
    tooltip: "info_brown_paper_percentage"
  }

  const plasticFoilsPerBaleEntries = extractPlasticFoilsPerBale(flattenedDataPerLabelset);
  tableEntries.plasticFoilsPerBale = {
    title: "plastic_foils_per_bale",
    supplierEntries: plasticFoilsPerBaleEntries,
    formatterHeader: (value) => formatNumber(value, 2),
    formatter: (value) => formatNumber(value, 2),
    betaBadge: true,
    tooltip: "info_plastic_foils_per_bale"
  }

  const balesWithPlasticFoilsEntries = extractBalesWithPlasticFoils(flattenedDataPerLabelset);
  tableEntries.balesWithPlasticFoils = {
    title: "bales_with_plastic_foils",
    supplierEntries: balesWithPlasticFoilsEntries,
    formatterHeader: (value) => formatPercentage(value, 1),
    formatter: (value) => formatPercentage(value, 1),
    betaBadge: true,
    tooltip: "info_bales_with_plastic_foils"
  }

  const contaminationLevelEntries = extractContaminationLevel(flattenedDataPerLabelset);
  tableEntries.contaminationLevel = {
    title: "contamination_level",
    supplierEntries: contaminationLevelEntries,
    formatterHeader: (value) => formatNumber(value, 2),
    formatter: (value) => formatNumber(value, 2),
    betaBadge: true,
    tooltip: "info_contamination_level"
  }

  const tableEntriesWithoutEmptyColumns = filterOutEmptyTableColumns(tableEntries);

  calculateAverages(tableEntriesWithoutEmptyColumns);

  return tableEntriesWithoutEmptyColumns;
}

function flattenSupplierDataPerLabelset(supplierData) {
  // Return a flattened data object with supplierId as the key and the aggregatedObservations as the value.
  const flattenedData = {};
  Object.entries(supplierData).forEach(([supplierId, supplierData]) => {
    flattenedData[supplierId] = {
      data_per_labelset: supplierData?.aggregatedObservations?.data_per_labelset,
      total_count: supplierData?.aggregatedObservations?.total_count,
      total_weight_kg: supplierData?.aggregatedObservations?.total_weight_kg,
    }
  });
  return flattenedData;
}

function filterOutEmptyTableColumns(tableEntries) {
  // Return a tableEntries object without entries that have all undefined values.
  return Object.fromEntries(
    Object.entries(tableEntries).filter(([_, tableEntry]) => {
      return !Object.values(tableEntry.supplierEntries).every(value => value === undefined);
    })
  )
}

function calculateAverages(tableEntries) {
  // Calculate averages for each table entry and add them to the table entry object.
  // Don't consider undefined values.
  Object.entries(tableEntries).forEach(([_, tableEntry]) => {
    let sum = 0;
    let count = 0;
    Object.entries(tableEntry.supplierEntries).forEach(([_, value]) => {
      if (value !== undefined) {
        sum += value;
        count++;
      }
    });
    tableEntry.total = sum;
    tableEntry.average = sum / count;
  });
}

/**
 * Sort supplier IDs based on the selected column and direction
 * @param {Array} suppliers - Array of supplier objects
 * @param {Object} tableEntries - Object containing table entries
 * @param {string} sortColumn - Column to sort by
 * @param {string} sortDirection - Direction to sort by
 */
export function getSortedSupplierIds(suppliers, tableEntries, sortColumn, sortDirection) {
  const ids = suppliers.map(supplier => supplier.id);

  function compareIds(idA, idB, sortDirection) {
    let valA, valB;
    if (sortColumn === 'name') {
      const supA = suppliers.find(s => s.id === idA);
      const supB = suppliers.find(s => s.id === idB);
      valA = supA?.display_name || supA?.name || '';
      valB = supB?.display_name || supB?.name || '';
    } else {
      const col = tableEntries[sortColumn];
      valA = col?.supplierEntries[idA];
      valB = col?.supplierEntries[idB];
    }

    // Push missing values to the bottom (no matter the sort direction)
    const aMissing = valA == null || valA == undefined || valA == '-';
    const bMissing = valB == null || valB == undefined || valB == '-';
    if (aMissing && bMissing) return 0;
    if (aMissing) return sortDirection === 'asc' ? 1 : -1;
    if (bMissing) return sortDirection === 'asc' ? -1 : 1;

    // Dynamic sort: strings alphabetically, numbers numerically
    if (typeof valA === 'string' && typeof valB === 'string') {
      return valA.localeCompare(valB, undefined, { sensitivity: 'base' });
    }
    if (typeof valA === 'number' && typeof valB === 'number') {
      return valA - valB;
    }
    // mixed or other types: fallback to string comparison
    return String(valA).localeCompare(String(valB), undefined, { sensitivity: 'base' });
  };
  const sorted = [...ids].sort((a, b) => compareIds(a, b, sortDirection));
  if (sortDirection === 'desc') {
    sorted.reverse();
  }
  return sorted;
}

// --------------------------------------------------------------------------
// ---------------------------- EXTRACT FUNCTIONS ----------------------------
// --------------------------------------------------------------------------

function extractNumberOfDeliveries(supplierDeliveriesData) {
  return Object.fromEntries(
    Object.entries(supplierDeliveriesData).map(([supplierId, data]) => {
      return [supplierId, getSupplierNumberOfDeliveries(data)];
    })
  )
}

function extractTotalWeightTons(supplierDeliveriesData) {
  return Object.fromEntries(
    Object.entries(supplierDeliveriesData).map(([supplierId, data]) => {
      return [supplierId, getSupplierTotalWeightTons(data)];
    })
  )
}

function extractNumberOfClaims(supplierDeliveriesData) {
  return Object.fromEntries(
    Object.entries(supplierDeliveriesData).map(([supplierId, data]) => {
      return [supplierId, getSupplierNumberOfClaims(data)];
    })
  )
}

function extractClaimsPerDelivery(numberOfClaimsTableEntries, numberOfDeliveriesTableEntries) {
  return Object.fromEntries(
    Object.entries(numberOfClaimsTableEntries).map(([supplierId, numberOfClaims]) => {
      const numberOfDeliveries = numberOfDeliveriesTableEntries?.[supplierId] || 0;
      if (numberOfDeliveries === 0) {
        return [supplierId, undefined];
      } else {
        return [supplierId, numberOfClaims / numberOfDeliveries];
      }
    })
  )
}

function extractClaimsPer20Tons(numberOfClaimsTableEntries, totalWeightTonsTableEntries) {
  return Object.fromEntries(
    Object.entries(numberOfClaimsTableEntries).map(([supplierId, numberOfClaims]) => {
      const totalWeightTons = totalWeightTonsTableEntries?.[supplierId] || 0;
      if (totalWeightTons === 0) {
        return [supplierId, undefined];
      } else {
        return [supplierId, numberOfClaims / totalWeightTons * 20];
      }
    })
  )
}

function extractTotalBalesInProduction(supplierObservationsData) {
  return Object.fromEntries(
    Object.entries(supplierObservationsData).map(([supplierId, data]) => {
      return [supplierId, data?.total_count];
    })
  )
}

function extractBrownPaperContent(supplierObservationsData) {
  const brownPaperContentEntries = {};
  Object.entries(supplierObservationsData).forEach(([supplierId, data]) => {
    brownPaperContentEntries[supplierId] = data?.data_per_labelset?.brown_paper_content?.average;
  });
  return brownPaperContentEntries;
}

function extractBalesWithPlasticFoils(supplierObservationsData) {
  const balesWithPlasticFoilsEntries = {};
  Object.entries(supplierObservationsData).forEach(([supplierId, data]) => {
    const count = data?.data_per_labelset?.contaminants?.plastics?.count || 0;
    const num_bales = data?.total_count;
    if (!num_bales) {
      balesWithPlasticFoilsEntries[supplierId] = undefined;
    } else {
      balesWithPlasticFoilsEntries[supplierId] = count / num_bales;
    }
  });
  return balesWithPlasticFoilsEntries;
}

function extractPlasticFoilsPerBale(supplierObservationsData) {
  const plasticFoilsPerBaleEntries = {};
  Object.entries(supplierObservationsData).forEach(([supplierId, data]) => {
    const count = data?.data_per_labelset?.contaminants?.plastics?.detection_count || 0;
    const num_bales = data?.total_count;
    if (!num_bales) {
      plasticFoilsPerBaleEntries[supplierId] = undefined;
    } else {
      plasticFoilsPerBaleEntries[supplierId] = count / num_bales;
    }
  });
  return plasticFoilsPerBaleEntries;
}

function extractContaminationLevel(supplierObservationsData) {
  const contaminationLevelEntries = {};
  Object.entries(supplierObservationsData).forEach(([supplierId, data]) => {
    const totalContaminants = data?.data_per_labelset?.contaminants?.plastics?.detection_count || 0;
    const contaminatedBales = data?.data_per_labelset?.contaminants?.plastics?.count || 0;
    if (!contaminatedBales) {
      contaminationLevelEntries[supplierId] = undefined;
    } else {
      contaminationLevelEntries[supplierId] = totalContaminants / contaminatedBales;
    }
  });
  return contaminationLevelEntries;
}

// --------------------------------------------------------------------------
// ---------------- FORMATTERS AND STYLERS FOR TABLE ENTRIES ----------------
// --------------------------------------------------------------------------

function formatNumber(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return value?.toLocaleString('de-CH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }) ?? '-';
}

function formatPercentage(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return `${(value * 100).toFixed(decimals)}%`;
}

function styleClaimsCount(formattedValue, value) {
  let className = 'status-success';
  if (value > 3) className = 'status-error';
  else if (value > 1) className = 'status-warning';
  else if (value > 0) className = 'status-neutral';
  return <span className={className}>{formattedValue}</span>
}

function styleClaimsPer20Tons(formattedValue, value) {
  let className = 'text-gray';
  if (value > 1) className = 'text-red';
  else if (value > 0.5) className = 'text-yellow';
  else if (value > 0) className = 'text-blue';
  return <span className={className}>{formattedValue}</span>
}

// --------------------------------------------------------------------------
// ---------------------------- EXPORT FUNCTIONS ----------------------------
// --------------------------------------------------------------------------

/**
 * Exports supplier data to Excel
 * @param {Array} suppliers - Array of supplier objects
 * @param {Object} tableEntries - Table entries from both deliveries and observations
 * @param {string} fileName - Name of the file to be downloaded
 * @param {Function} translate - Translation function for column titles
 */
export function exportToExcel(suppliers, tableEntries, fileName = 'supplier_data.xlsx', translate = (key) => key) {
  try {
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Prepare headers
    const headers = [translate('supplier_name')];
    const columnIds = ['name'];
    
    // Add all table entries as columns
    Object.entries(tableEntries).forEach(([columnId, columnData]) => {
      headers.push(translate(columnData.title));
      columnIds.push(columnId);
    });
    
    // Prepare data rows
    const rows = [];
    rows.push(headers); // Add headers as first row
    
    // Create average row
    const averageRow = ['Average'];
    for (let i = 1; i < columnIds.length; i++) {
      const columnId = columnIds[i];
      const columnData = tableEntries[columnId];
      
      if (columnData && columnData.average !== undefined) {
        const formatter = columnData.formatterHeader || columnData.formatter || (val => val);
        const formattedAverage = formatter(columnData.average);
        averageRow.push(formattedAverage === '-' ? '' : columnData.average); // Use raw value for Excel
      } else {
        averageRow.push('');
      }
    }
    rows.push(averageRow); // Add average as second row
    
    // Sort suppliers alphabetically by name
    const sortedSuppliers = [...suppliers].sort((a, b) => {
      const nameA = (a.display_name || a.name || '').toLowerCase();
      const nameB = (b.display_name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    // Add data for each supplier
    sortedSuppliers.forEach(supplier => {
      const row = [];
      const supplierId = supplier.id;
      
      // Add supplier name
      row.push(supplier.display_name || supplier.name);
      
      // Add data for each column
      for (let i = 1; i < columnIds.length; i++) {
        const columnId = columnIds[i];
        const columnData = tableEntries[columnId];
        
        if (columnData) {
          const value = columnData.supplierEntries[supplierId];
          
          // Apply formatter without styling
          if (value !== undefined && value !== null) {
            // Use raw numeric value for Excel, not the formatted display value
            if (typeof value === 'number') {
              row.push(value);
            } else {
              // For other types, use the formatted value
              const formatter = columnData.formatter || (val => val);
              const formattedValue = formatter(value);
              row.push(formattedValue === '-' ? '' : formattedValue);
            }
          } else {
            row.push('');
          }
        } else {
          row.push('');
        }
      }
      
      rows.push(row);
    });
    
    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(rows);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Supplier Data');
    
    // Generate Excel file
    XLSX.writeFile(wb, fileName);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('Failed to generate Excel file. Please try again later.');
  }
}